import React, { useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import styles from "./AgendaItemDetails.module.css";
import { AGENDAITEM_PROPERTY } from "src/stores/assemblies/AgendaItemStore";
import { CurrentAgendaItemDocuments } from "./documents/CurrentAgendaItemDocuments";
import { AddAgendaDocumentsModal } from "src/components/assemblies/agenda/list/add-documents-modal/AddAgendaDocumentsModal";
import { CustomerType } from "src/components/sidebar/Sidebar";
import { runInAction } from "mobx";

enum ProtocolTagKeys {
    Accepted = "screens.assemblies.agenda.tags.accepted",
    Rejected = "screens.assemblies.agenda.tags.rejected",
    ValueQuota = "screens.assemblies.agenda.tags.valueQuota",
    Owner = "screens.assemblies.agenda.tags.owner",
    Decision = "screens.assemblies.agenda.tags.decision"
}

const AgendaItemDetailsBase = observer(() => {
    const { t } = useTranslation();
    const { agendaitemStore, assemblyStore, navStore } = useContext(RootStoreContext);

    const onChangeAgendaItemField = useCallback(
        (agendaItemProperty: AGENDAITEM_PROPERTY) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;

            if (agendaitemStore.currentAgendaItem) {
                agendaitemStore.updateAgendaItem(agendaItemProperty, newValue);
            }

            agendaitemStore.setAgendaItemsHaveChanged();
        },
        [agendaitemStore]
    );

    const documentTile = () => {
        if (
            agendaitemStore.currentAgendaItem?.agendaitemfiles &&
            agendaitemStore.currentAgendaItem?.agendaitemfiles.length > 0
        ) {
            return <CurrentAgendaItemDocuments />;
        }
    };

    const onClickDocumentIcon = useCallback(
        async (e: any) => {
            e.preventDefault();

            assemblyStore.setIsAddingDocumentsDisplayed(true);
        },
        [assemblyStore]
    );

    const onClickProtocolTag = useCallback(
        (translationKey: string) => (e: any) => {
            const textToAdd = t(translationKey);
            runInAction(() => {
                if (agendaitemStore.currentAgendaItem) {
                    const currentText = agendaitemStore.currentAgendaItem.protocoltext || "";
                    agendaitemStore.currentAgendaItem.protocoltext = currentText
                        ? `${currentText} ${textToAdd} `
                        : `${textToAdd} `;
                }
            });
            // After updating, set focus to the textarea
            const textArea = document.getElementById("agendaProtocolText");
            if (textArea) {
                (textArea as HTMLTextAreaElement).focus();
            }
        },
        [agendaitemStore.currentAgendaItem, t]
    );

    const protocolTag = () => {
        return Object.values(ProtocolTagKeys).map((translationKey) => (
            <div key={translationKey} className={styles.Tags} onClick={onClickProtocolTag(translationKey)}>
                {t(translationKey)} <UI.Icon icon={UI.SVGIcon.Add} size={12} className={styles.AddIconTags} />
            </div>
        ));
    };

    return (
        <div className={styles.ExpandedContent}>
            <div className={styles.AgendaTitle}>{t("screens.assemblies.agenda.public_declarations")}</div>
            <UI.Input
                className={styles.AgendaInput}
                value={agendaitemStore.currentAgendaItem?.text || ""}
                onChange={onChangeAgendaItemField(AGENDAITEM_PROPERTY.TEXT)}
                label={`${t("screens.assemblies.agenda.title")} *`}
                disabled={agendaitemStore.isAgendaItemLocked}
            />
            <UI.Input
                as="textarea"
                className={styles.AgendaItemDescription}
                value={agendaitemStore.currentAgendaItem?.description || ""}
                onChange={onChangeAgendaItemField(AGENDAITEM_PROPERTY.DESCRIPTION)}
                label={`${t("screens.assemblies.agenda.description")} *`}
                disabled={agendaitemStore.isAgendaItemLocked}
            />
            <UI.Input
                as="textarea"
                className={styles.AgendaItemComment}
                value={agendaitemStore.currentAgendaItem?.internalcomments || ""}
                onChange={onChangeAgendaItemField(AGENDAITEM_PROPERTY.INTERNALCOMMENTS)}
                label={t("screens.assemblies.agenda.internalcomments").toString()}
                disabled={agendaitemStore.isAgendaItemLocked}
                placeholder={t("screens.assemblies.agenda.internalcomments_placeholder").toString()}
            />
            {navStore.customer.type === CustomerType.TEST ? (
                <div className={styles.ProtocolContainer}>
                    <label className={styles.ProtocolLabel}>
                        {t("screens.assemblies.agenda.protocoltext").toString()}
                    </label>
                    <div
                        className={
                            agendaitemStore.isAgendaItemLocked
                                ? styles.ProtocolTagsContainerLocked
                                : styles.ProtocolTagsContainer
                        }
                    >
                        {agendaitemStore.isAgendaItemLocked ? undefined : (
                            <div className={agendaitemStore.isAgendaItemLocked ? undefined : styles.ProtocolTags}>
                                {protocolTag()}
                            </div>
                        )}

                        <textarea
                            id="agendaProtocolText"
                            className={styles.AgendaItemProtocoltext}
                            value={agendaitemStore.currentAgendaItem?.protocoltext || ""}
                            onChange={onChangeAgendaItemField(AGENDAITEM_PROPERTY.PROTOCOLTEXT)}
                            placeholder={
                                agendaitemStore.isAgendaItemLocked
                                    ? ""
                                    : t("screens.assemblies.agenda.protocol_placeholder").toString()
                            }
                            disabled={agendaitemStore.isAgendaItemLocked}
                        />
                    </div>
                </div>
            ) : undefined}

            {agendaitemStore.isAgendaItemLocked ? undefined : (
                <>
                    <div className={styles.AddDocument} onClick={onClickDocumentIcon}>
                        <UI.Icon icon={UI.SVGIcon.Add} size={14} className="mr-2" />
                        {t("screens.assemblies.agenda.add_document")}
                    </div>
                    <> {assemblyStore.isAgendaItemTemplate === false ? <AddAgendaDocumentsModal /> : undefined}</>
                </>
            )}

            {documentTile()}
        </div>
    );
});

export const AgendaItemDetails = Sentry.withProfiler(AgendaItemDetailsBase);
